/**
 * Shared search-param helpers for auth pages (login / signup).
 */

export type AuthPageSearchParams = {
  callbackUrl?: string | string[];
  ref?: string | string[];
  inviteEmail?: string | string[];
};

/**
 * Returns a safe relative path or null if the value is missing/suspicious.
 * Guards against open-redirect attacks via protocol-relative or backslash URLs.
 */
export function sanitizeCallbackUrl(rawValue: string | null): string | null {
  if (!rawValue) return null;
  if (!rawValue.startsWith("/")) return null;
  if (rawValue.startsWith("//")) return null;
  if (rawValue.includes("\\")) return null;
  return rawValue;
}

/** Return the first value when the query param can be a string or string[]. */
export function getFirstQueryValue(
  value: string | string[] | undefined,
): string | null {
  if (Array.isArray(value)) return value[0] ?? null;
  return value ?? null;
}

/**
 * Build a URLSearchParams containing only safe, validated redirect params.
 * - `callbackUrl` must be a relative path (no protocol-relative or backslash tricks).
 * - `ref` is passed through as-is when present.
 */
export function buildRedirectQuery(
  searchParams?: AuthPageSearchParams,
): URLSearchParams {
  const callbackUrl = getFirstQueryValue(searchParams?.callbackUrl);
  const ref = getFirstQueryValue(searchParams?.ref);
  const query = new URLSearchParams();

  if (
    callbackUrl &&
    callbackUrl.startsWith("/") &&
    !callbackUrl.startsWith("//") &&
    !callbackUrl.includes("\\")
  ) {
    query.set("callbackUrl", callbackUrl);
  }
  if (ref) {
    query.set("ref", ref);
  }

  const inviteEmail = getFirstQueryValue(searchParams?.inviteEmail);
  if (inviteEmail) {
    query.set("inviteEmail", inviteEmail);
  }

  return query;
}
